// HSE Incident / Near Miss Form — strictly factual draft. No blame, no
// auto-classification, no severity grading, no legal conclusion, no root-cause
// declaration unless the user explicitly entered "suspected" or "under review".
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v && String(v).trim() ? String(v).trim() : MISSING);

export const HSE_REMINDER =
  "Follow company incident reporting, client requirements, and applicable regulatory notification procedures. This draft does not replace formal incident investigation or HSE reporting requirements.";

// Defensive language sanitiser — strips obvious blame phrases the user might
// inadvertently type so they don't propagate into the polished draft. The tool
// also does not infer severity or classification.
const BLAME_RX = /\b(at fault|to blame|culpable|guilty|negligent|negligence|reckless)\b/gi;
const sanitiseBlame = (s) => (s ? s.replace(BLAME_RX, "[neutral wording recommended]") : s);

export function buildHseIncidentReport(state) {
  const h = state.header || {};
  const det = state.details || {};
  const people = state.people || {};
  const actions = state.actions || {};
  const evidence = state.evidence || {};
  const fu = state.followUp || {};

  const lines = [];
  const push = (s) => { if (s !== null && s !== undefined) lines.push(s); };
  const sec = (label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

  sec("Reminder");
  push(HSE_REMINDER);

  sec("Report Header");
  push(`Report Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}  ·  Position: ${orMissing(h.position)}`);
  push(`Event Type (as user-entered, not classified by this tool): ${orMissing(h.eventType)}`);

  sec("Event Details");
  push(`Date / Time of Event: ${orMissing(det.dateTime)}`);
  push(`Location (as entered): ${orMissing(det.location)}`);
  push(`Activity in Progress (as entered): ${orMissing(det.activity)}`);
  const desc = polishProse(sanitiseBlame(det.description));
  push(`Brief Description (factual, neutral): ${desc || MISSING}`);

  sec("Persons Involved (Text Only)");
  const inv = polishProse(sanitiseBlame(people.involved));
  push(`Involved: ${inv || MISSING}`);
  const wit = polishProse(sanitiseBlame(people.witnesses));
  push(`Witnesses: ${wit || MISSING}`);
  push("Names recorded for company internal reporting only. No blame inferred by this tool.");

  sec("Immediate Actions Taken");
  push(polishProse(actions.immediate) || MISSING);

  sec("Persons Informed");
  push(polishProse(actions.personsInformed) || MISSING);

  sec("Photos / Evidence Noted");
  push(polishProse(evidence.notes) || MISSING);

  sec("Suspected / Under-Review Notes");
  push("This tool does not assign severity, root cause, or incident class. Only user-entered notes appear below.");
  const sus = polishProse(sanitiseBlame(state.suspected?.notes));
  push(sus || MISSING);

  sec("Follow-Up Required");
  push(`Follow-Up Required: ${fu.required ? "Yes" : "No"}`);
  const fuDetail = polishProse(fu.detail);
  if (fuDetail) push(`Follow-Up Detail: ${fuDetail}`);
  if (fu.companyProcedureRef) push(`Company Procedure Reference (as entered): ${fu.companyProcedureRef}`);

  sec("Final Reminder");
  push(HSE_REMINDER);

  return lines.join("\n");
}

export function hseReviewChecklist(state) {
  const out = [];
  const h = state.header || {};
  const det = state.details || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.preparedBy) out.push("Prepared-By missing");
  if (!h.eventType) out.push("Event type missing (user-entered, not classified)");
  if (!det.dateTime) out.push("Date / time of event missing");
  if (!det.location) out.push("Location missing");
  if (!det.description) out.push("Brief description missing");
  if (!state.actions?.immediate) out.push("Immediate actions taken missing");
  if (!state.actions?.personsInformed) out.push("Persons informed missing");
  return out;
}

export const HSE_QUICK_PHRASES = [
  "Event observed and recorded.",
  "Immediate area secured.",
  "Personnel accounted for.",
  "First-aid administered as user-entered.",
  "Site supervisor informed.",
  "Operator company representative informed.",
  "Photos taken and stored as evidence.",
  "Follow-up actions pending under company procedure.",
  "Root cause under review.",
  "Severity classification under review by competent personnel.",
];
